import { Injectable, NgZone, OnDestroy, Renderer2 } from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TranslateService } from '@ngx-translate/core';

import { TransformStore } from '@mhe/reader/state/transform';
import * as transformActions from '@mhe/reader/state/transform/transform.actions';
import { Book, SpineItem } from '@mhe/reader/models';
import { Next, Transformer } from './transformer';
import { DevicePlatform, DeviceService } from '@mhe/reader/common';

@Injectable()
export class SelectionTransformer implements Transformer, OnDestroy {
  private unlistenerRegistry: Record<string, () => void> = {};
  private activeSelection: Selection | undefined;
  private shiftHeld = false;

  constructor(
    private readonly renderer: Renderer2,
    private readonly zone: NgZone,
    private readonly transformStore: TransformStore,
    private readonly deviceService: DeviceService,
    private readonly liveAnnouncer: LiveAnnouncer,
    private readonly translateService: TranslateService,
  ) {}

  async postRender(
    book: Book,
    spineItem: SpineItem,
    content: HTMLDocument,
    iframe: HTMLIFrameElement,
    next: Next,
  ): Promise<HTMLDocument> {
    const devicePlatform = this.deviceService.platformType;

    this.unlistenerRegistry[iframe.id]?.();

    const selectionEndFn = (e): void => {
      if (this.shiftHeld) return;

      this.zone.run(() => {
        const selection = content.getSelection();
        const details: {
          nonTextFlag?: boolean
          type: 'math' | 'media' | 'widget' | 'image'
        } = e?.detail || null;

        if (selection?.type?.toLowerCase() !== 'range') {
          this.activeSelection?.removeAllRanges();
          this.activeSelection = undefined;
          this.transformStore.dispatch(transformActions.rangeRemoved());
        }

        if (selection?.type?.toLowerCase() === 'range') {
          if (this.activeSelection && this.activeSelection !== selection) {
            this.activeSelection?.removeAllRanges();
          }

          this.activeSelection = selection;
          this.transformStore.dispatch(
            transformActions.rangeSelected({
              selection,
              devicePlatform,
              details,
            }),
          );
        }
      });
    };

    const unlistenCaretBrowsing = this.listenCaretBrowsing(content, iframe);

    if (devicePlatform !== DevicePlatform.Desktop) {
      const unlistenSelectionChange = this.renderer.listen(content, 'selectionchange', selectionEndFn);
      this.unlistenerRegistry[iframe.id] = () => {
        unlistenSelectionChange();
        unlistenCaretBrowsing();
      };
    } else {
      const unlistenMouseup = this.renderer.listen(content, 'mouseup', selectionEndFn);
      this.unlistenerRegistry[iframe.id] = () => {
        unlistenMouseup();
        unlistenCaretBrowsing();
      };
    }

    return await next(content);
  }

  ngOnDestroy(): void {
    Object.values(this.unlistenerRegistry).forEach((unlistener) =>
      unlistener(),
    );
  }

  /**
   * Registers all keyboard and blur listeners that support caret-browsing
   * (Shift+Arrow selection, Enter/Space activation, hint tooltip, dismiss).
   * Returns a single teardown function that removes all listeners.
   */
  private listenCaretBrowsing(content: HTMLDocument, iframe: HTMLIFrameElement): () => void {
    const unlistenActivation = this.renderer.listen(content, 'keydown', (e: KeyboardEvent) => {
      this.handleActivationKey(e, content);
    });

    const unlistenKeydown = this.renderer.listen(content, 'keydown', (e: KeyboardEvent) => {
      this.handleCaretKeydown(e, content);
    });

    const unlistenKeyup = this.renderer.listen(content, 'keyup', (e: KeyboardEvent) => {
      this.handleCaretKeyup(e, content, iframe);
    });

    const unlistenBlur = this.renderer.listen(iframe.contentWindow, 'blur', () => {
      this.shiftHeld = false;
      this.zone.run(() => {
        this.transformStore.dispatch(transformActions.caretSelectionHintHide());
      });
    });

    return () => {
      unlistenActivation();
      unlistenKeydown();
      unlistenKeyup();
      unlistenBlur();
    };
  }

  /** Enter/Space with an active range opens the annotation toolbar (Desktop mode). */
  private handleActivationKey(e: KeyboardEvent, content: HTMLDocument): void {
    const isActivationKey = e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar';
    if (!isActivationKey) return;

    const selection = content.getSelection();
    if (selection?.type?.toLowerCase() === 'range') {
      e.preventDefault();
      this.zone.run(() => {
        this.transformStore.dispatch(
          transformActions.rangeSelected({
            selection,
            devicePlatform: DevicePlatform.Desktop,
          }),
        );
      });
    }
  }

  /**
   * Handles keydown events for caret-browsing state:
   * - Shift pressed: mark selection in progress, hide tooltip.
   * - Shift+Arrow (shiftKey held): no-op (selection extending).
   * - Other keys: dismiss selection and hide tooltip.
   */
  private handleCaretKeydown(e: KeyboardEvent, content: HTMLDocument): void {
    if (e.key === 'Shift') {
      this.shiftHeld = true;
      this.zone.run(() => {
        this.transformStore.dispatch(transformActions.caretSelectionHintHide());
      });
      return;
    }

    // Activation keys are handled separately.
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') return;

    // While Shift is held, arrows extend the selection — don't dismiss.
    if (e.shiftKey) return;

    // Any other key dismisses the selection.
    const selection = content.getSelection();
    if (selection?.type?.toLowerCase() === 'range') {
      this.zone.run(() => {
        this.activeSelection?.removeAllRanges();
        this.activeSelection = undefined;
        this.transformStore.dispatch(transformActions.rangeRemoved());
      });
    }
  }

  /**
   * On Shift keyup, if a range is selected, show the caret-selection hint
   * tooltip centered on the selection and announce for screen readers.
   */
  private handleCaretKeyup(e: KeyboardEvent, content: HTMLDocument, iframe: HTMLIFrameElement): void {
    if (e.key !== 'Shift') return;
    this.shiftHeld = false;

    const selection = content.getSelection();
    if (selection?.type?.toLowerCase() !== 'range') return;

    this.zone.run(() => {
      const message = this.translateService.instant('context.caret_selection_hint');
      void this.liveAnnouncer.announce(message, 'assertive');

      const rangeRect = selection.getRangeAt(0).getBoundingClientRect();
      const iframeRect = iframe.getBoundingClientRect();
      const top = iframeRect.top + rangeRect.top;
      const left = iframeRect.left + rangeRect.left + rangeRect.width / 2;
      this.transformStore.dispatch(transformActions.caretSelectionHintShow({ top, left }));
    });
  }
}
